import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Button from '@shared/atoms/Button';
import Input from '@shared/atoms/Input';
import Select from '@shared/atoms/Select';
import T from '@shared/atoms/Typography';
import LanguageSelect from '@shared/molecules/LanguageSelect';

import theme from '@shared/themes/default';
import { EXAM_TYPE, GAME_TYPE, courseStatus } from '@shared/const';

import { useCourse } from '../../../../contexts/CourseContext';

import TestContent from './components/TestContent';

const SectionStyled = styled.section`
  background: ${theme.palette.darkWrapper};
  padding: ${theme.spacing.m1} ${theme.spacing.s};
`;
const TopWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const LeftWrapper = styled.div`
  display: flex;
  gap: ${theme.spacing.m1};
`;

const ContentWrapper = styled.div`
  padding: ${theme.spacing.m1} 0 0;
`;

const Message = styled.div`
  align-self: end;
`;

const InputButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${theme.spacing.s};
  width: fit-content;
`;

const Exam = () => {
  const {
    course,
    enabledLanguages,
    validateGameQuery,
    validateGameResponse,
    hasChanged,
    locked,
    saveExam,
    setHandleSave,
    setHasChanged,
    submitting,
  } = useCourse();

  const { exam, status, spokenLanguage } = course;

  let defaultType = EXAM_TYPE.NONE;
  if (exam) {
    // eslint-disable-next-line no-underscore-dangle
    defaultType = exam.__typename === 'Game' ? EXAM_TYPE.GAME : EXAM_TYPE.TEST;
  }

  const { t } = useTranslation();

  const [selectedTypeExam, setSelectedTypeExam] = useState(defaultType);
  const [currentExam, setCurrentExam] = useState(exam || {});
  const [hasError, setHasError] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  const [gameName, setGameName] = useState(false);
  const [isRoleplay, setIsRoleplay] = useState(false);
  const [isEditing, setIsEditing] = useState(defaultType === EXAM_TYPE.TEST);
  const [selectedQuizLanguage, setSelectedQuizLanguage] = useState(
    enabledLanguages && enabledLanguages.length > 0
      ? enabledLanguages[0]
      : spokenLanguage.code
  );

  let options = [
    {
      value: EXAM_TYPE.TEST,
      key: EXAM_TYPE.TEST,
      label: t('backoffice.courses.examTab.multipleType'),
    },
    {
      value: EXAM_TYPE.GAME,
      key: EXAM_TYPE.GAME,
      label: t('backoffice.courses.examTab.gameType'),
    },
  ];

  if (defaultType === EXAM_TYPE.NONE || status === courseStatus.DRAFT) {
    options = [
      {
        value: EXAM_TYPE.NONE,
        key: EXAM_TYPE.NONE,
        label: t('backoffice.courses.examTab.noExamType'),
      },
      ...options,
    ];
  }

  const onChange = ({ value }) => {
    setSelectedTypeExam(value);
    setCurrentExam(exam || {});
    setHasChanged(value !== defaultType && value === EXAM_TYPE.NONE);
    setHasError(false);
    setGameName(null);
  };

  const onChangeGameId = (externalGameId) => {
    setCurrentExam({ ...(exam || {}), externalGameId });
    setIsValidated(false);
    setGameName(null);
  };

  const handleSave = async () => {
    const newExam = {
      ...currentExam,
      courseId: course.id,
      questions:
        selectedTypeExam === EXAM_TYPE.TEST ? currentExam.questions : null,
      externalGameId:
        selectedTypeExam === EXAM_TYPE.TEST ? null : currentExam.externalGameId,
      type: selectedTypeExam,
    };

    if (
      currentExam?.questions?.length === 0 &&
      selectedTypeExam !== EXAM_TYPE.GAME
    ) {
      newExam.type = EXAM_TYPE.NONE;
    }

    if (selectedTypeExam === EXAM_TYPE.TEST) {
      newExam.questions = newExam.questions.map((question, i) => ({
        ...question,
        ordinal: i,
      }));
    }

    saveExam(newExam);
    setHasChanged(false);
    setIsEditing(selectedTypeExam === EXAM_TYPE.TEST);
  };

  const validateGameId = async () => {
    if (currentExam.externalGameId.length > 0) {
      validateGameQuery({
        variables: { gameId: currentExam.externalGameId },
      });
    }
  };

  useEffect(() => {
    setCurrentExam(exam || {});
  }, [exam]);

  useEffect(() => {
    const { data, error, loading } = validateGameResponse;

    if (!loading && (error || data)) {
      if (!error) {
        const { validateGame } = data;

        setGameName(validateGame.name);
        setIsRoleplay(validateGame.type === GAME_TYPE.ROLEPLAY);
        setHasChanged(
          validateGame.name &&
            validateGame.type !== GAME_TYPE.ROLEPLAY &&
            exam?.externalGameId !== currentExam.externalGameId
        );
      }

      setIsValidated(true);
    }
  }, [validateGameResponse]);

  useEffect(() => {
    setHandleSave(() => handleSave);
  }, [selectedTypeExam, currentExam]);

  useEffect(() => {
    if (selectedTypeExam === EXAM_TYPE.GAME && !!currentExam.externalGameId) {
      validateGameId(currentExam.externalGameId);
    } else {
      setIsValidated(false);
    }
  }, [selectedTypeExam]);

  const disableSave =
    locked ||
    !hasChanged ||
    hasError ||
    (selectedTypeExam === EXAM_TYPE.TEST &&
      currentExam.questions?.length === 0 &&
      status !== courseStatus.DRAFT) ||
    (selectedTypeExam === EXAM_TYPE.GAME &&
      (currentExam.externalGameId?.length === 0 ||
        !isValidated ||
        gameName === null ||
        isRoleplay));

  return (
    <SectionStyled>
      <TopWrapper>
        <LeftWrapper>
          <div>
            <T
              color={theme.palette.lightPure}
              sx={{ marginBottom: theme.spacing.xs }}
            >
              {t('backoffice.courses.examTab.examTypeLabel')}
            </T>
            <Select
              disabled={locked || submitting || validateGameResponse.loading}
              id="typeQuizSelect"
              items={options}
              name="typeQuizSelect"
              onChange={onChange}
              value={selectedTypeExam}
              sx={{ width: '240px' }}
              theme="dark"
              required
            />
          </div>
          {selectedTypeExam === EXAM_TYPE.TEST &&
            enabledLanguages.length > 1 && (
              <div>
                <T
                  color={theme.palette.lightPure}
                  sx={{ marginBottom: theme.spacing.xs }}
                >
                  {t('backoffice.courses.examTab.translationLanguage')}
                </T>
                <LanguageSelect
                  languages={enabledLanguages}
                  onChange={({ value }) => setSelectedQuizLanguage(value)}
                  required
                  value={selectedQuizLanguage}
                  theme="dark"
                  sx={{ width: '180px' }}
                />
              </div>
            )}
          {selectedTypeExam === EXAM_TYPE.NONE && (
            <Message>
              <T>
                <T component="span" fontWeight="semibold">
                  {t('backoffice.courses.examTab.noExamMessage1')}
                </T>{' '}
                {t('backoffice.courses.examTab.noExamMessage2')}
              </T>
              <T>
                {t('backoffice.courses.examTab.noExamMessage3')}{' '}
                <T component="span" fontWeight="semibold">
                  {t('backoffice.courses.examTab.noExamMessage1')}
                </T>{' '}
                {t('backoffice.courses.examTab.noExamMessage4')}
              </T>
            </Message>
          )}
        </LeftWrapper>
        <Button
          ariaLabel={t('generic.save')}
          disabled={disableSave}
          loading={submitting}
          onClick={handleSave}
          sx={{ height: '40px' }}
          type="secondary"
        >
          {t('generic.save')}
        </Button>
      </TopWrapper>
      {selectedTypeExam === EXAM_TYPE.TEST && (
        <ContentWrapper>
          <TestContent
            defaultTest={exam}
            disabled={locked}
            isEditing={isEditing}
            language={spokenLanguage.code}
            languages={enabledLanguages}
            selectedLanguage={selectedQuizLanguage}
            modifyTest={setCurrentExam}
            setHasError={setHasError}
            setHasModified={setHasChanged}
            setIsEditing={setIsEditing}
            test={currentExam}
          />
        </ContentWrapper>
      )}
      {selectedTypeExam === EXAM_TYPE.GAME && (
        <ContentWrapper>
          <T
            color={theme.palette.lightPure}
            sx={{ marginBottom: theme.spacing.xs }}
          >
            {t('backoffice.courses.examTab.gameLabel')}
          </T>
          <InputButtonWrapper>
            <Input
              disabled={locked}
              onChange={(e) => onChangeGameId(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onBlur={validateGameId}
              placeholder={t('backoffice.courses.examTab.gamePlaceHolder')}
              sx={{ width: '240px' }}
              value={currentExam?.externalGameId || ''}
            />
            <Button
              ariaLabel={t('backoffice.courses.examTab.validateGameId')}
              loading={validateGameResponse.loading}
              disabled={
                !currentExam?.externalGameId ||
                currentExam?.externalGameId?.length === 0 ||
                isValidated
              }
              onClick={validateGameId}
              sx={{ height: '40px' }}
              type="secondary"
            >
              {t('backoffice.courses.examTab.validateGameId')}
            </Button>
          </InputButtonWrapper>
          {isValidated && gameName && !isRoleplay && (
            <T
              color={theme.palette.lightPure}
              sx={{ marginTop: theme.spacing.s }}
            >
              {gameName}
            </T>
          )}
          {isValidated && gameName && isRoleplay && (
            <T
              color={theme.palette.errorDark}
              sx={{ marginTop: theme.spacing.s }}
            >
              {t('backoffice.courses.examTab.roleplayError')}
            </T>
          )}
          {isValidated && !gameName && (
            <T
              color={theme.palette.errorDark}
              sx={{ marginTop: theme.spacing.s }}
            >
              {t('backoffice.courses.examTab.checkError')}
            </T>
          )}
        </ContentWrapper>
      )}
    </SectionStyled>
  );
};

export default Exam;
